import { useCallback, useEffect, useState } from "react";
import { eventById } from "@zend/content";
import { GameIcon } from "../assets/gameIcons.js";
import { PromoArt } from "../store/PromoArt.js";
import { onWs } from "../api/ws.js";
import { pick, useT } from "../i18n/index.js";
import { navigate } from "../router.js";
import { Icon as FunctionalIcon } from "../ui/Icon.js";
import { Timer } from "../ui/Timer.js";
import { worldApi } from "./api.js";
import { useWorld } from "./state.js";
import type { HuntView } from "./types.js";

const POLL_MS = 60_000;

/** Hourly hunt: a chip (map) with the live window, kills and countdown or the next start; as a card it replaces the generic event card on the base today strip (D68). */
export function HuntChip({ className = "", variant = "chip" }: { className?: string; variant?: "chip" | "card" }) {
  const t = useT();
  const [hunt, setHunt] = useState<HuntView | null>(null);
  const load = useCallback(() => {
    worldApi.hunt().then(setHunt).catch(() => undefined);
  }, []);
  useEffect(() => {
    load();
    const id = window.setInterval(load, POLL_MS);
    const offs = [onWs("event.start", load), onWs("event.end", load), onWs("hunt.kill", load)];
    return () => {
      window.clearInterval(id);
      for (const off of offs) off();
    };
  }, [load]);
  if (!hunt) return null;
  const jump = () => {
    const target = hunt.targets[0];
    if (target) {
      useWorld.getState().setJump({ x: target.x, y: target.y });
      navigate("/app/map");
    } else navigate("/app/events");
  };
  if (variant === "card") {
    if (!hunt.active) return null;
    const total = hunt.kills + hunt.targets.length;
    return (
      <button type="button" className="today-chip glass event-action hot" onClick={jump} data-testid="hunt-chip" data-active="true">
        <PromoArt id="event-frontier" className="dock-backdrop" />
        <GameIcon id="emote.bolt" size={64} />
        <span className="reward-action-copy">
          <small>{t("events.hunt.live")}</small>
          <strong>{pick(eventById("ev.hourly_hunt").name)}</strong>
          <span className="mono">{t("events.hunt.targets", { n: hunt.kills, m: total })}{hunt.endsAt ? <> · <Timer finishesAt={hunt.endsAt} onDone={load} /></> : null}</span>
        </span>
      </button>
    );
  }
  if (hunt.active) {
    const target = hunt.targets[0];
    const total = hunt.kills + hunt.targets.length;
    return (
      <button
        type="button"
        className={`chip warn hunt-chip ${className}`}
        onClick={() => {
          if (target) {
            useWorld.getState().setJump({ x: target.x, y: target.y });
            navigate("/app/map");
          } else navigate("/app/events");
        }}
        data-testid="hunt-chip"
        data-active="true"
      >
        <FunctionalIcon name="search" /> {t("events.hunt.live")} · {t("events.hunt.targets", { n: hunt.kills, m: total })}
        {hunt.endsAt ? <> · <Timer finishesAt={hunt.endsAt} onDone={load} /></> : null}
      </button>
    );
  }
  if (!hunt.nextAt) return null;
  return (
    <button type="button" className={`chip hunt-chip ${className}`} onClick={() => navigate("/app/events")} data-testid="hunt-chip" data-active="false">
      <FunctionalIcon name="clock" /> {t("events.hunt.next")} <Timer finishesAt={hunt.nextAt} onDone={load} />
    </button>
  );
}
